import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Hammer, Leaf, UtensilsCrossed, Brush, Tent, Car, PaintBucket, Baseline as Pipeline, Zap, ChevronRight, Search, Ruler, HardHat, Construction, Sparkles, Package, Trees } from 'lucide-react';
import { useQuery } from '@apollo/client';
import Button from '../components/ui/Button';
import { GET_CATEGORIES } from '../graphql/queries/categories';
import { Category } from '../types/Category';

// Function to determine color based on category name
const getCategoryColor = (name: string, code: string): string => {
  const nameLower = name.toLowerCase();
  
  // Check predefined mappings first
  const codeColorMap: Record<string, string> = {
    'power-tools': 'bg-blue-500',
    'hand-tools': 'bg-orange-500',
    'garden-lawn': 'bg-green-500',
    'kitchen': 'bg-red-500',
    'cleaning': 'bg-purple-500',
    'outdoor': 'bg-green-600',
    'automotive': 'bg-gray-600',
    'painting': 'bg-pink-500',
    'plumbing': 'bg-indigo-500',
    'electrical': 'bg-blue-400',
  };
  
  if (codeColorMap[code]) {
    return codeColorMap[code];
  }
  
  // Intelligent color matching based on keywords
  if (nameLower.includes('have') || nameLower.includes('plæne') || nameLower.includes('udendørs')) {
    return 'bg-green-500'; // Nature/outdoor = green
  }
  if (nameLower.includes('el-') || nameLower.includes('elektrisk')) {
    return 'bg-blue-500'; // Electrical = blue
  }
  if (nameLower.includes('murer') || nameLower.includes('beton') || nameLower.includes('cement')) {
    return 'bg-gray-500'; // Concrete/masonry = gray
  }
  if (nameLower.includes('tømrer') || nameLower.includes('byg') || nameLower.includes('træ')) {
    return 'bg-amber-600'; // Carpentry/wood = amber/brown
  }
  if (nameLower.includes('rengøring') || nameLower.includes('oprydning')) {
    return 'bg-purple-500'; // Cleaning = purple
  }
  if (nameLower.includes('måle') || nameLower.includes('præcision')) {
    return 'bg-blue-600'; // Measuring = blue
  }
  if (nameLower.includes('lift') || nameLower.includes('stillads') || nameLower.includes('stige')) {
    return 'bg-yellow-500'; // Lift/scaffolding = yellow (safety)
  }
  if (nameLower.includes('hånd')) {
    return 'bg-orange-500'; // Hand tools = orange
  }
  if (nameLower.includes('bil') || nameLower.includes('automotive')) {
    return 'bg-gray-600'; // Automotive = gray
  }
  if (nameLower.includes('maling') || nameLower.includes('maler')) {
    return 'bg-pink-500'; // Painting = pink
  }
  if (nameLower.includes('vvs') || nameLower.includes('rør')) {
    return 'bg-indigo-500'; // Plumbing = indigo
  }
  if (nameLower.includes('køkken')) {
    return 'bg-red-500'; // Kitchen = red
  }
  if (nameLower.includes('forbrugs')) {
    return 'bg-slate-500'; // Consumables = slate
  }
  
  // Default fallback
  return 'bg-gray-500';
};

// Function to determine icon based on category name
const getCategoryIcon = (name: string, code: string): JSX.Element => {
  const nameLower = name.toLowerCase();
  
  // Check predefined mappings first
  const codeIconMap: Record<string, JSX.Element> = {
    'power-tools': <Wrench className="h-6 w-6" />,
    'hand-tools': <Hammer className="h-6 w-6" />,
    'garden-lawn': <Leaf className="h-6 w-6" />,
    'kitchen': <UtensilsCrossed className="h-6 w-6" />,
    'cleaning': <Brush className="h-6 w-6" />,
    'outdoor': <Tent className="h-6 w-6" />,
    'automotive': <Car className="h-6 w-6" />,
    'painting': <PaintBucket className="h-6 w-6" />,
    'plumbing': <Pipeline className="h-6 w-6" />,
    'electrical': <Zap className="h-6 w-6" />,
  };
  
  if (codeIconMap[code]) {
    return codeIconMap[code];
  }
  
  // Intelligent icon matching based on keywords
  if (nameLower.includes('have') || nameLower.includes('udendørs')) {
    return <Trees className="h-6 w-6" />; // Garden = Trees
  }
  if (nameLower.includes('el-') || nameLower.includes('elektrisk')) {
    return <Zap className="h-6 w-6" />; // Electrical = Zap
  }
  if (nameLower.includes('murer') || nameLower.includes('beton')) {
    return <HardHat className="h-6 w-6" />; // Masonry = Hard hat
  }
  if (nameLower.includes('tømrer') || nameLower.includes('byg')) {
    return <HardHat className="h-6 w-6" />; // Carpentry = Hard hat
  }
  if (nameLower.includes('rengøring') || nameLower.includes('oprydning')) {
    return <Sparkles className="h-6 w-6" />; // Cleaning = Sparkles
  }
  if (nameLower.includes('måle')) {
    return <Ruler className="h-6 w-6" />; // Measuring = Ruler
  }
  if (nameLower.includes('lift') || nameLower.includes('stillads')) {
    return <Construction className="h-6 w-6" />; // Lift/scaffolding = Construction
  }
  if (nameLower.includes('hånd')) {
    return <Hammer className="h-6 w-6" />; // Hand tools = Hammer
  }
  if (nameLower.includes('bil') || nameLower.includes('automotive')) {
    return <Car className="h-6 w-6" />; // Automotive = Car
  }
  if (nameLower.includes('maling') || nameLower.includes('maler')) {
    return <PaintBucket className="h-6 w-6" />; // Painting = Paint bucket
  }
  if (nameLower.includes('vvs') || nameLower.includes('rør')) {
    return <Pipeline className="h-6 w-6" />; // Plumbing = Pipeline
  }
  if (nameLower.includes('køkken')) {
    return <UtensilsCrossed className="h-6 w-6" />; // Kitchen = Utensils
  }
  if (nameLower.includes('forbrugs')) {
    return <Package className="h-6 w-6" />; // Consumables = Package
  }
  
  // Default fallback
  return <Wrench className="h-6 w-6" />;
};

// Mapping for category codes to examples
const categoryStyleMap: Record<string, { examples: string[] }> = {
  'power-tools': {
    examples: ['Boremaskiner', 'Slibemaskiner', 'Save'],
  },
  'hand-tools': {
    examples: ['Hammere', 'Skruenøgler', 'Skruetrækkere'],
  },
  'garden-lawn': {
    examples: ['Plæneklippere', 'Trimmere', 'Haveværktøj'],
  },
  'kitchen': {
    examples: ['Køkkenmaskiner', 'Foodprocessorer', 'Specialudstyr'],
  },
  'cleaning': {
    examples: ['Højtryksrensere', 'Tæpperensere', 'Damprensere'],
  },
  'outdoor': {
    examples: ['Campingudstyr', 'Stiger', 'Højtryksrensere'],
  },
  'automotive': {
    examples: ['Donkrafte', 'Diagnoseværktøj', 'Kompressorer'],
  },
  'painting': {
    examples: ['Malersprøjter', 'Stiger', 'Afdekningsmateriale'],
  },
  'plumbing': {
    examples: ['Rørtænger', 'Afløbsslanger', 'Rørskærere'],
  },
  'electrical': {
    examples: ['Multimetre', 'Afstrippere', 'Kredsløbstestere'],
  },
};

const Categories: React.FC = () => {
  const { data, loading, error } = useQuery<{ categories: Category[] }>(GET_CATEGORIES);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Indlæser kategorier...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600">Kunne ikke indlæse kategorier. Prøv igen senere.</p>
          </div>
        </div>
      </div>
    );
  }

  const categories = data?.categories || [];

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Værktøjskategorier</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Gennemse vores omfattende samling af værktøj på tværs af forskellige kategorier
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => {
            const style = categoryStyleMap[category.code] || {
              examples: [],
            };
            
            const categoryColor = getCategoryColor(category.name, category.code);
            const categoryIcon = getCategoryIcon(category.name, category.code);

            // Parse description into bullet points if it contains commas
            const descriptionItems = category.description 
              ? category.description.split(',').map(item => item.trim()).filter(item => item.length > 0)
              : [];
            
            // Use examples from style map if available, otherwise use parsed description
            const bulletPoints = style.examples.length > 0 ? style.examples : descriptionItems;

            return (
              <Link
                key={category.id}
                to={`/browse?category=${encodeURIComponent(category.name)}`}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg ${categoryColor} text-white`}>
                      {categoryIcon}
                    </div>
                    <span className="text-sm text-gray-500">
                      {category.enabledToolCount || 0} værktøj
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{category.name}</h3>
                  {bulletPoints.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {bulletPoints.map((item, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <ChevronRight className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                          <span>{item.charAt(0).toUpperCase() + item.slice(1)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-6">
                    <Button
                      variant="outline"
                      fullWidth
                      className="justify-between"
                    >
                      <span>Gennemse {category.name}</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Categories;